/**
 * C端用户登录页 - 手机号+验证码
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Phone, Shield, ArrowRight, Loader2 } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { engagementService } from '../services/engagementService'
import { useToast } from '../components/Toast'
import { authService } from '../services/authService'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuth()
  const toast = useToast()

  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sendingCode, setSendingCode] = useState(false)
  const [loginLoading, setLoginLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const redirectTo = (location.state as any)?.from || '/'

  // 已登录用户重定向
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  // 清理倒计时定时器
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
      }
    }
  }, [])

  const handleSendCode = async () => {
    if (!phone || phone.length !== 11) {
      toast.warning('请输入正确的手机号')
      return
    }

    setSendingCode(true)
    try {
      await authService.sendSmsCode(phone)
      toast.success('验证码已发送')
      setCountdown(60)
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current)
              countdownTimerRef.current = null
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (error: any) {
      toast.error(error.message || '发送验证码失败')
    } finally {
      setSendingCode(false)
    }
  }

  const handleLogin = async () => {
    if (!phone || phone.length !== 11) {
      toast.warning('请输入正确的手机号')
      return
    }
    if (!code || code.length < 4) {
      toast.warning('请输入验证码')
      return
    }

    setLoginLoading(true)
    try {
      await login(phone, code)
      // 迁移本地收藏到服务端（跨设备同步）
      void engagementService.migrateLocalFavorites('me').catch(() => 0)
      toast.success('登录成功')
      navigate(redirectTo)
    } catch (error: any) {
      toast.error(error.message || '登录失败')
    } finally {
      setLoginLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cyber-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyber-neon to-cyber-pink rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">P</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Phygital</h1>
          <p className="text-gray-400 mt-2">登录您的账户</p>
        </div>

        {/* Form */}
        <div className="cyber-card rounded-2xl p-8 space-y-6">
          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">手机号</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="请输入手机号"
                className="w-full pl-10 pr-4 py-3 bg-cyber-dark border border-cyber-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyber-neon transition-colors"
              />
            </div>
          </div>

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">验证码</label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="请输入验证码"
                  className="w-full pl-10 pr-4 py-3 bg-cyber-dark border border-cyber-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyber-neon transition-colors"
                />
              </div>
              <button
                onClick={handleSendCode}
                disabled={sendingCode || countdown > 0}
                className="px-4 py-3 bg-cyber-neon/20 border border-cyber-neon/30 rounded-lg text-cyber-neon text-sm font-medium hover:bg-cyber-neon/30 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {sendingCode ? '发送中...' : countdown > 0 ? `${countdown}s` : '获取验证码'}
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={loginLoading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyber-neon to-cyber-pink rounded-lg text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loginLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                登录中...
              </>
            ) : (
              <>
                登录
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
          <div className="mt-4 text-center text-sm text-gray-500">
            品牌方？<a href="/admin/" className="text-cyber-neon hover:underline">进入商家工作台</a>
            <span className="mx-1 text-gray-700">·</span>
            <a href="/admin/register" className="text-gray-400 hover:underline">申请入驻</a>
          </div>

          <p className="text-center text-xs text-gray-500">
            登录即表示同意《用户协议》和《隐私政策》
          </p>
        </div>
      </div>
    </div>
  )
}
